import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { ScreenshotUploader } from './ScreenshotUploader';
import { CopyLinkButton } from './UnlockGate';
import { BROKERS, PAYMENTS, TRAINING_FEE_USD, getFlutterwaveLink, getMinDeposit } from '../config/brand';
import { trackBrokerClick } from '../lib/track';
import { Gift, CreditCard, ExternalLink, ArrowLeft, ArrowRight, Coins, Wallet, CheckCircle2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const card = { backgroundColor: 'var(--background-elevated)', border: '1px solid var(--border-subtle)' };

const Step = ({ n, children, testId }) => (
    <div className="flex gap-3 items-start rounded-xl p-3" style={card} data-testid={testId}>
        <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 gold-button-primary">{n}</span>
        <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{children}</div>
    </div>
);

function PathChooser({ onChoose }) {
    const { t } = useTranslation();
    const options = [
        { path: 'cpa', Icon: Gift, cls: 'breathing-card-free', badgeCls: 'badge-free-pop', title: t('unlock.cpaTitle'), badge: t('unlock.cpaBadge'), desc: t('unlock.cpaDesc', { min: getMinDeposit() }), points: t('unlock.cpaPoints', { returnObjects: true }), tid: 'unlock-path-cpa' },
        { path: 'paid', Icon: CreditCard, cls: 'breathing-card-paid', badgeCls: 'badge-paid-pop', title: t('unlock.paidTitle', { fee: TRAINING_FEE_USD }), badge: t('unlock.paidBadge'), desc: t('unlock.paidDesc'), points: t('unlock.paidPoints', { returnObjects: true }), tid: 'unlock-path-paid' },
    ];
    return (
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4 py-1" data-testid="unlock-path-chooser">
            {options.map(({ path, Icon, cls, badgeCls, title, badge, desc, points, tid }) => (
                <button key={path} type="button" onClick={() => onChoose(path)} className={`${cls} text-start p-3 sm:p-5 flex flex-col gap-2 sm:gap-3`} data-testid={tid}>
                    <div className="flex items-start justify-between gap-1.5">
                        <span className="icon-bubble-gold w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0"><Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#FFD700' }} /></span>
                        <span className={`${badgeCls} text-[9px] sm:text-[10px] px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full whitespace-nowrap`}>{badge}</span>
                    </div>
                    <div>
                        <p className="text-sm sm:text-base font-black leading-tight" style={{ color: 'var(--text-primary)', fontFamily: "'Outfit', sans-serif" }}>{title}</p>
                        <p className="text-[11px] sm:text-xs leading-snug sm:leading-relaxed mt-1 sm:mt-1.5 line-clamp-4 sm:line-clamp-none" style={{ color: 'var(--text-secondary)' }}>{desc}</p>
                    </div>
                    <ul className="space-y-1 sm:space-y-1.5 mt-auto">
                        {(Array.isArray(points) ? points : []).map((pt) => (
                            <li key={pt} className="flex items-start gap-1.5 sm:gap-2 text-[10px] sm:text-[11px] font-semibold leading-tight" style={{ color: 'var(--text-primary)' }}><Sparkles className="w-3 h-3 shrink-0 mt-px" style={{ color: '#FFD700' }} />{pt}</li>
                        ))}
                    </ul>
                    <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-black mt-1" style={{ color: '#FFD700' }}>{t('unlock.choose')} <ArrowRight className="w-3.5 h-3.5 rtl-flip" /></span>
                </button>
            ))}
        </div>
    );
}

function FreePath({ onDone }) {
    const { t } = useTranslation();
    const b = BROKERS.exness;
    return (
        <div className="space-y-3" data-testid="unlock-free-path">
            <Step n={1} testId="free-step-1">
                {t('unlock.freeStep1')}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <a href={b.affiliateUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackBrokerClick('unlock_dialog', 'cpa')} className="inline-flex items-center gap-1.5 font-bold" style={{ color: 'var(--primary-color)' }} data-testid="unlock-broker-link">{t('unlock.openBroker', { broker: b.name })} <ExternalLink className="w-3.5 h-3.5" /></a>
                    <CopyLinkButton url={b.affiliateUrl} testId="unlock-copy-link-btn" onTrack={() => trackBrokerClick('unlock_dialog_copy', 'cpa')} />
                </div>
            </Step>
            <Step n={2} testId="free-step-2"><Trans i18nKey="unlock.freeStep2" values={{ min: getMinDeposit() }} components={{ 1: <strong /> }} /></Step>
            <Step n={3} testId="free-step-3">{t('unlock.freeStep3')}</Step>
            <ScreenshotUploader ctaLabel={t('unlock.uploadAccount')} onSuccess={onDone} />
        </div>
    );
}

function PaidPath({ onMethod, onDone }) {
    const { t } = useTranslation();
    const [method, setMethod] = useState(null);
    const fw = getFlutterwaveLink();
    const pick = (m) => { setMethod(m); onMethod(m); };
    return (
        <div className="space-y-3" data-testid="unlock-paid-path">
            <p className="text-xs text-center" style={{ color: 'var(--text-secondary)' }}><Trans i18nKey="unlock.paidIntro" values={{ fee: TRAINING_FEE_USD }} components={{ 1: <strong /> }} /></p>
            <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => pick(fw.method)} className="rounded-xl p-3 text-start" style={{ ...card, borderColor: method?.startsWith('flutterwave') ? 'var(--primary-color)' : undefined }} data-testid="pay-method-flutterwave">
                    <Wallet className="w-4 h-4 mb-1" style={{ color: 'var(--primary-color)' }} /><p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('unlock.cardBank')}</p><p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Flutterwave · {fw.label}</p>
                </button>
                <button type="button" onClick={() => pick('usdt_trc20')} className="rounded-xl p-3 text-start" style={{ ...card, borderColor: method?.startsWith('usdt') ? 'var(--primary-color)' : undefined }} data-testid="pay-method-crypto">
                    <Coins className="w-4 h-4 mb-1" style={{ color: 'var(--primary-color)' }} /><p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('unlock.crypto')}</p><p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{t('unlock.cryptoSub')}</p>
                </button>
            </div>
            {method?.startsWith('flutterwave') && (
                <Step n={1} testId="paid-flutterwave-step">
                    {t('unlock.fwStep')}
                    <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }} data-testid="flutterwave-currency-note">{t('unlock.localCurrency', { currency: fw.currency })}</p>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <a href={fw.url} target="_blank" rel="noopener noreferrer" data-testid="flutterwave-pay-link"><Button size="sm" className="btn-primary">{fw.label} <ExternalLink className="w-3.5 h-3.5 ms-1.5" /></Button></a>
                        <img src={fw.qr} alt="Flutterwave QR" className="w-28 h-36 rounded-lg object-contain bg-white border" style={{ borderColor: 'var(--border-subtle)' }} data-testid="flutterwave-qr" />
                        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{t('unlock.scanToPay')}</span>
                    </div>
                </Step>
            )}
            {method?.startsWith('usdt') && (
                <div className="space-y-2" data-testid="paid-crypto-step">
                    {PAYMENTS.crypto.map((c) => (
                        <div key={c.id} className="rounded-xl p-3" style={{ ...card, borderColor: method === c.id ? 'var(--primary-color)' : undefined }} onClick={() => pick(c.id)} role="button" data-testid={`crypto-${c.id}`}>
                            <div className="flex items-center justify-between mb-1"><p className="text-xs font-black" style={{ color: 'var(--text-primary)' }}>{c.coin} · {c.network}</p><CopyLinkButton url={c.address} testId={`copy-${c.id}`} label={t('unlock.copyAddress')} /></div>
                            <div className="flex items-center gap-3">
                                <img src={c.qr} alt={`${c.network} QR`} className="w-24 h-32 rounded-md object-contain bg-white border shrink-0" style={{ borderColor: 'var(--border-subtle)' }} data-testid={`qr-${c.id}`} />
                                <p className="text-[11px] font-mono break-all" style={{ color: 'var(--text-secondary)' }}>{c.address}</p>
                            </div>
                        </div>
                    ))}
                    <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{t('unlock.cryptoNote', { fee: TRAINING_FEE_USD })}</p>
                </div>
            )}
            {method && (
                <>
                    <Step n={2} testId="paid-upload-step">{t('unlock.paidUploadStep')}</Step>
                    <ScreenshotUploader ctaLabel={t('unlock.uploadPayment')} onSuccess={onDone} />
                </>
            )}
        </div>
    );
}

export function UnlockPaths({ onUploaded }) {
    const { t } = useTranslation();
    const { user, refreshAuth } = useAuth();
    const [path, setPath] = useState(user?.unlock_path || null);
    const [done, setDone] = useState(false);
    const save = (p, method) => axios.post(`${API}/auth/unlock-path`, { path: p, method }, { withCredentials: true }).catch(() => {});
    const choose = (p) => { setPath(p); save(p, p === 'cpa' ? 'cpa' : undefined); };
    const finish = async (d) => { setDone(true); await refreshAuth(); onUploaded?.(d); };

    if (done) {
        return (
            <div className="rounded-2xl p-5 text-center" style={card} data-testid="unlock-proof-received">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#059669' }} />
                <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{t('unlock.received')}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('unlock.receivedSub')}</p>
            </div>
        );
    }
    if (!path) return <PathChooser onChoose={choose} />;
    return (
        <div className="space-y-3">
            <button type="button" onClick={() => setPath(null)} className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: 'var(--text-secondary)' }} data-testid="unlock-back-btn"><ArrowLeft className="w-3.5 h-3.5" /> {t('unlock.back')}</button>
            {path === 'cpa' ? <FreePath onDone={finish} /> : <PaidPath onMethod={(m) => save('paid', m)} onDone={finish} />}
        </div>
    );
}
